import React, { useState, useRef } from 'react'
import {
  CurrencyDollarIcon,
  BanknotesIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  DocumentTextIcon,
  PlusCircleIcon,
  EyeIcon
} from '@heroicons/react/24/outline'
import { CountdownTimer, PaymentDeadlineCountdown } from '../ui/CountdownTimer'
import { Payment, PaymentStatus } from '../../types'
import { paymentService, BankTransferInfo, PaymentInitiationInfo, UploadPaymentReceiptData, CreatePaymentData } from '../../services/payment.service'
import PaymentConfirmationModal from './PaymentConfirmationModal'

interface BankTransferPaymentProps {
  caseId: string
  caseNumber: string
  totalAmount: number
  payments?: Payment[]
  onPaymentComplete?: () => void
}

export function BankTransferPayment({
  caseId,
  caseNumber,
  totalAmount,
  payments = [],
  onPaymentComplete
}: BankTransferPaymentProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [bankTransferInfo, setBankTransferInfo] = useState<BankTransferInfo | null>(null)
  const [paymentInitInfo, setPaymentInitInfo] = useState<PaymentInitiationInfo | null>(null)
  const [selectedBank, setSelectedBank] = useState<string | null>(null)
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null)
  const [receiptFile, setReceiptFile] = useState<File | null>(null)
  const [receiptUploaded, setReceiptUploaded] = useState(false)
  const [uploadedReceipts, setUploadedReceipts] = useState<Payment[]>([])
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [bankAccountWithFullDetails, setBankAccountWithFullDetails] = useState<any | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Filter pending payments
  const pendingPayments = payments.filter(p => p.status === PaymentStatus.PENDING)
  const hasPendingPayment = pendingPayments.length > 0

  // Create a combined list of payments that need receipt upload
  const allPendingForUpload = [...pendingPayments]
  if (paymentInitInfo?.existingPayment && !payments.some(p => p.id === paymentInitInfo.existingPayment.id)) {
    allPendingForUpload.push(paymentInitInfo.existingPayment as Payment)
  }
  const isPaidInFull = payments.some(p => p.status === PaymentStatus.PAID)

  // Check if any payment has a receipt uploaded
  const paymentsWithReceipts = [...allPendingForUpload, ...payments, ...uploadedReceipts]
  const hasReceiptUpload = paymentsWithReceipts.some(p => p && ((p.transferProofUrl && p.transferProofUrl.length > 0) || (p.receiptUrl && p.receiptUrl.length > 0))) || receiptUploaded

  
  React.useEffect(() => {
    loadPaymentInitiationInfo()
  }, [caseId])

  const loadPaymentInitiationInfo = async () => {
    try {
      setIsLoading(true)
      const info = await paymentService.getPaymentInitiationInfo(caseId)
      setPaymentInitInfo(info)

      // If there's an existing payment, load the bank transfer info
      if (info.existingPayment) {
        const bankInfo = await paymentService.getBankTransferInfo(caseId)
        setBankTransferInfo(bankInfo)
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load payment information')
    } finally {
      setIsLoading(false)
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type and size
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']
      const maxSize = 10 * 1024 * 1024 // 10MB

      if (!validTypes.includes(file.type)) {
        setError('Please upload a valid image (JPG, PNG) or PDF file')
        return
      }

      if (file.size > maxSize) {
        setError('File size must be less than 10MB')
        return
      }

      setReceiptFile(file)
      setError(null)
    }
  }

  const handleUploadReceipt = async (paymentId: string) => {
    if (!receiptFile) {
      setError('Please select a receipt file to upload')
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const uploadData: UploadPaymentReceiptData = {
        paymentId,
        receiptFile
      }

      const updatedPayment = await paymentService.uploadPaymentReceipt(uploadData)

      setSuccess('Receipt uploaded successfully! Your payment is being verified.')
      setReceiptFile(null)
      setReceiptUploaded(true)

      // Add to uploaded receipts
      setUploadedReceipts(prev => [...prev, updatedPayment])

      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      // Refresh payment info
      await loadPaymentInitiationInfo()

      setTimeout(() => {
        setSuccess(null)
        onPaymentComplete?.()
      }, 3000)

    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload receipt')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreatePayment = async () => {
    if (!selectedBank) {
      setError('Please select a bank account to transfer to')
      return
    }

    try {
      // Fetch complete bank account details for the selected bank
      const bankAccountsFullDetails = await paymentService.getBankAccountsWithFullDetails([selectedBank])
      const selectedBankAccount = bankAccountsFullDetails.find(account => account.id === selectedBank)

      if (selectedBankAccount) {
        setBankAccountWithFullDetails(selectedBankAccount)
        setShowConfirmationModal(true)
      } else {
        setError('Selected bank account not found')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch bank account details')
    }
  }

  const handleConfirmPayment = async () => {
    try {
      setIsLoading(true)
      setError(null)

      const paymentData: CreatePaymentData = {
        caseId,
        amount: totalAmount,
        description: `Bank transfer payment for case ${caseNumber}`,
        paymentMethod: 'BANK_TRANSFER',
        reference: `CASE-${caseNumber}`
      }

      await paymentService.createPayment(paymentData)

      setSuccess('Payment initiated successfully! Please complete the bank transfer using the details below.')
      setShowConfirmationModal(false)

      // Refresh payment info to load bank transfer details
      await loadPaymentInitiationInfo()

      // Trigger parent component to refresh payments immediately
      onPaymentComplete?.()

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null)
      }, 3000)

    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to initiate payment')
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusColor = (status: PaymentStatus): string => {
    return paymentService.getPaymentStatusColor(status)
  }

  const getBankIcon = (bankName: string): string => {
    const bankIcons: Record<string, string> = {
      'maybank': '🏦',
      'cimb': '🏛️',
      'public bank': '🏧',
      'rhb': '🏤',
      'hong leong': '🏥',
      'ambank': '🏦',
      'affin bank': '🏛️'
    }
    return bankIcons[bankName.toLowerCase()] || '🏦'
  }

  if (isPaidInFull) {
    return (
      <div className="card">
        <div className="card-body text-center py-8">
          <CheckCircleIcon className="h-16 w-16 text-green-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-neutral-900 mb-2">Payment Complete</h3>
          <p className="text-neutral-600">
            Thank you! Your payment for case {caseNumber} has been completed and verified.
          </p>
        </div>
      </div>
    )
  }

  if (isLoading && !paymentInitInfo) {
    return (
      <div className="card">
        <div className="card-body text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading payment information...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Booking Approval Status - Show if bookings are not approved */}
      {paymentInitInfo?.bookingInfo && !paymentInitInfo.bookingInfo.canInitiatePayment && (
        <div className="card">
          <div className="card-body text-center py-8">
            <ClockIcon className="h-16 w-16 text-yellow-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-neutral-900 mb-2">Booking Approval Pending</h3>
            <p className="text-neutral-600 mb-4">
              {paymentInitInfo.bookingInfo.unapprovedBooking?.status === 'PENDING'
                ? 'Your bookings are being reviewed by our admin team. Please wait for approval before making payment.'
                : paymentInitInfo.bookingInfo.unapprovedBooking?.status === 'REJECTED'
                ? 'Your booking has been rejected. Please contact support for assistance.'
                : paymentInitInfo.bookingInfo.unapprovedBooking?.status === 'CANCELLED'
                ? 'Your booking has been cancelled. Please contact support for assistance.'
                : 'Your bookings are currently being processed. You will be able to make payment once they are approved.'
              }
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
              <h4 className="font-medium text-neutral-900 mb-2">Booking Status:</h4>
              <div className="space-y-3">
                {paymentInitInfo.bookingInfo.bookings.map((booking) => (
                  <div key={booking.id} className="border-l-4 border-neutral-200 pl-4">
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <span className="font-medium text-neutral-900">
                          {paymentService.getServiceName(booking.serviceType)}
                        </span>
                        <div className="text-xs text-neutral-500 mt-1">
                          Provider: {paymentService.getProviderName(booking.serviceProvider)}
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        booking.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                        booking.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                        booking.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                        booking.status === 'CANCELLED' ? 'bg-gray-100 text-gray-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {booking.status.charAt(0) + booking.status.slice(1).toLowerCase()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Summary - Only show if user has payments or if bookings are approved */}
      {(payments.length > 0 || paymentInitInfo?.existingPayment || (paymentInitInfo?.bookingInfo?.canInitiatePayment)) && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-neutral-900 flex items-center">
              <CurrencyDollarIcon className="h-5 w-5 mr-2 text-primary-600" />
              Payment Summary
            </h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-neutral-600">Case Number:</span>
                <span className="font-medium">{caseNumber}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-neutral-600">Total Amount:</span>
                <span className="font-bold text-lg text-primary-600">RM {totalAmount.toFixed(2)}</span>
              </div>
              {paymentInitInfo && (
                <PaymentDeadlineCountdown dueDate={paymentInitInfo.paymentDueDate} />
              )}
              {hasPendingPayment && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex items-center text-yellow-800">
                    <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                    <span className="text-sm font-medium">Payment Pending</span>
                  </div>
                  <p className="text-sm text-yellow-700 mt-1">
                    Please complete the bank transfer and upload your receipt below.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Initiate Bank Transfer - For cases with no payments yet */}
      {payments.length === 0 && !paymentInitInfo?.existingPayment && !isLoading && paymentInitInfo && paymentInitInfo.bookingInfo?.canInitiatePayment && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-neutral-900 flex items-center">
              <PlusCircleIcon className="h-5 w-5 mr-2 text-primary-600" />
              Select Bank for Transfer
            </h3>
            <p className="text-sm text-neutral-500 mt-1">
              Choose which bank account you would like to transfer to
            </p>
          </div>
          <div className="card-body">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center text-red-800">
                  <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">Error</span>
                </div>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                <div className="flex items-center text-green-800">
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">Success</span>
                </div>
                <p className="text-sm text-green-700 mt-1">{success}</p>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <h4 className="font-semibold text-neutral-900 mb-2">
                Payment Details
              </h4>
              <div className="bg-white rounded-md p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Case Number:</span>
                  <span className="font-semibold">{paymentInitInfo.caseInfo.caseNumber}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Deceased Name:</span>
                  <span className="font-semibold">{paymentInitInfo.caseInfo.deceasedName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Payment Amount:</span>
                  <span className="font-semibold text-primary-600">RM {totalAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-neutral-600 text-sm">Time Remaining:</span>
                  <CountdownTimer
                    targetDate={paymentInitInfo.paymentDueDate}
                    size="small"
                    className="text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-medium text-neutral-900">Available Bank Accounts</h4>
              {paymentInitInfo.bankAccounts.map((bank) => (
                <div
                  key={bank.id}
                  className={`relative border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedBank === bank.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                  onClick={() => setSelectedBank(bank.id)}
                >
                  <div className="flex items-start">
                    <div className="text-2xl mr-3 mt-1">
                      {getBankIcon(bank.bankName)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <p className="font-semibold text-neutral-900 text-lg">{bank.bankName}</p>
                            {bank.displayPriority === 1 && (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                Primary Account
                              </span>
                            )}
                          </div>
                          <div className="space-y-1">
                            <p className="text-sm text-neutral-600">
                              <span className="font-medium">Account Holder:</span> {bank.accountHolderName}
                            </p>
                            <div className="flex items-center space-x-4">
                              <p className="text-sm font-mono text-neutral-900 bg-neutral-100 px-2 py-1 rounded">
                                {bank.accountNumberLast}
                              </p>
                              {bank.currency && (
                                <span className="text-xs text-neutral-500 bg-neutral-100 px-2 py-1 rounded">
                                  {bank.currency}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-sm">
                            <p className="text-neutral-500">Minimum</p>
                            <p className="font-semibold text-neutral-900">RM {bank.minimumAmount.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                      {bank.description && (
                        <div className="mt-3 p-2 bg-blue-50 rounded-md">
                          <p className="text-sm text-blue-800">{bank.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={`absolute top-4 right-4 w-4 h-4 rounded-full border-2 ${
                    selectedBank === bank.id
                      ? 'border-primary-500 bg-primary-500'
                      : 'border-neutral-300'
                  }`}>
                    {selectedBank === bank.id && (
                      <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-primary-500"></div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <button
                onClick={handleCreatePayment}
                disabled={!selectedBank || isLoading}
                className="w-full btn-primary"
              >
                {isLoading ? 'Processing Payment...' : selectedBank ? 'Make Payment' : 'Select a Bank Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bank Transfer Instructions */}
      {bankTransferInfo && bankTransferInfo.bankAccount && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-neutral-900 flex items-center">
              <BanknotesIcon className="h-5 w-5 mr-2 text-primary-600" />
              Bank Transfer Instructions
            </h3>
            <p className="text-sm text-neutral-500 mt-1">
              Please transfer the payment to the designated account
            </p>
          </div>
          <div className="card-body">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">
                  {getBankIcon(bankTransferInfo.bankAccount.bankName)}
                </div>
                <div className="flex-1">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-neutral-700">Bank</p>
                      <p className="font-semibold text-neutral-900">
                        {bankTransferInfo.bankAccount.bankName}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-neutral-700">Account Name</p>
                      <p className="font-semibold text-neutral-900">
                        {bankTransferInfo.bankAccount.accountName}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-neutral-700">Account Number</p>
                      <p className="font-semibold text-lg text-primary-600 font-mono">
                        {bankTransferInfo.bankAccount.accountNumber}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-neutral-700">Reference Number</p>
                      <p className="font-semibold text-lg text-primary-600 font-mono">
                        {bankTransferInfo.referenceNumber}
                      </p>
                    </div>
                  </div>
                  {bankTransferInfo.dueDate && (
                    <div className="mt-4 p-3 bg-amber-50 rounded-md">
                      <div className="flex items-center text-amber-800">
                        <ClockIcon className="h-4 w-4 mr-2" />
                        <span className="text-sm font-medium">Payment Due</span>
                      </div>
                      <p className="text-sm text-amber-700">
                        Please complete payment before {new Date(bankTransferInfo.dueDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-neutral-50 rounded-lg">
              <h4 className="font-medium text-neutral-900 mb-2">Important Instructions:</h4>
              <ul className="text-sm text-neutral-700 space-y-1">
                <li>• Please use the exact reference number ({bankTransferInfo.referenceNumber}) when making the transfer</li>
                <li>• Ensure the payment amount matches RM {bankTransferInfo.amount.toFixed(2)}</li>
                <li>• Upload a clear receipt or transaction confirmation</li>
                <li>• Payment verification typically takes 1-2 business days</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Payment Status */}
      {payments.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-neutral-900 flex items-center">
              <DocumentTextIcon className="h-5 w-5 mr-2 text-primary-600" />
              Payment Status
            </h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              {payments.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between p-4 border border-neutral-200 rounded-lg">
                  <div>
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{paymentService.getPaymentStatusIcon(payment.status)}</span>
                      <div>
                        <p className="font-medium text-neutral-900">{payment.description}</p>
                        <p className="text-sm text-neutral-600">
                          {new Date(payment.createdAt).toLocaleDateString()}
                          {payment.paidAt && ` • Paid on ${new Date(payment.paidAt).toLocaleDateString()}`}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-neutral-900">RM {payment.amount.toFixed(2)}</p>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                      {paymentService.formatPaymentStatus(payment.status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Receipt Upload */}
      {(hasPendingPayment || paymentInitInfo?.existingPayment) && (
        <div className="card">
          <div className="card-header">
            {hasReceiptUpload ? (
              <>
                <h3 className="text-lg font-medium text-neutral-900 flex items-center">
                  <CheckCircleIcon className="h-5 w-5 mr-2 text-green-600" />
                  Receipt Uploaded
                </h3>
                <div className="flex items-center mt-1">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <ClockIcon className="h-3 w-3 mr-1" />
                    Awaiting Verification
                  </span>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium text-neutral-900 flex items-center">
                  <ArrowUpTrayIcon className="h-5 w-5 mr-2 text-primary-600" />
                  Upload Payment Receipt
                </h3>
                <p className="text-sm text-neutral-500 mt-1">
                  Upload a clear image or PDF of your transaction receipt
                </p>
              </>
            )}
          </div>
          <div className="card-body">
            {hasReceiptUpload ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <div className="flex items-center text-green-800">
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    <span className="text-sm font-medium">Receipt Uploaded Successfully</span>
                  </div>
                  <p className="text-sm text-green-700 mt-2">
                    Your payment receipt has been uploaded and is currently being reviewed by our admin team. You will be notified once the verification is complete.
                  </p>
                </div>

                {/* List of uploaded receipts */}
                {paymentsWithReceipts.filter(p => p && p.receiptUrl && p.receiptUrl.length > 0).length > 0 && (
                  <div className="bg-white border border-neutral-200 rounded-md p-4">
                    <p className="text-sm font-medium text-neutral-900 mb-3">Uploaded Receipts</p>
                    <div className="space-y-3">
                      {paymentsWithReceipts
                        .filter(p => p && p.receiptUrl && p.receiptUrl.length > 0)
                        .map((payment, index) => (
                          <div key={`${payment.id}-${index}`} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                            <div className="flex-1">
                              <p className="font-medium text-sm text-neutral-900">{payment.description}</p>
                              <p className="text-sm text-neutral-600">RM {payment.amount.toFixed(2)}</p>
                              <p className="text-xs text-neutral-500">
                                Uploaded {new Date(payment.updatedAt || payment.createdAt).toLocaleString()}
                              </p>
                            </div>
                            <button
                              onClick={() => window.open(payment.receiptUrl, '_blank')}
                              className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center"
                            >
                              <EyeIcon className="h-4 w-4 mr-1" />
                              View
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <p className="text-sm font-medium text-blue-900 mb-2">What happens next?</p>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Admin will review your payment receipt</li>
                    <li>• You'll receive a notification once verified</li>
                    <li>• Your account will be activated upon approval</li>
                  </ul>
                </div>

                <div className="text-center pt-2">
                  <button
                    onClick={() => {
                      setReceiptFile(null)
                      setError(null)
                      setSuccess(null)
                      setReceiptUploaded(false)
                      if (fileInputRef.current) {
                        fileInputRef.current.value = ''
                      }
                    }}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Upload Additional Receipt
                  </button>
                </div>
              </div>
            ) : (
              <>
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-center text-red-800">
                      <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                      <span className="text-sm font-medium">Error</span>
                    </div>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                )}

                {success && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex items-center text-green-800">
                      <CheckCircleIcon className="h-5 w-5 mr-2" />
                      <span className="text-sm font-medium">Success</span>
                    </div>
                    <p className="text-sm text-green-700 mt-1">{success}</p>
                  </div>
                )}

                <div className="space-y-4">
                  <div className="border-2 border-dashed border-neutral-300 rounded-lg p-6 text-center">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,application/pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="receipt-upload"
                    />
                    <label htmlFor="receipt-upload" className="cursor-pointer">
                      <ArrowUpTrayIcon className="h-12 w-12 text-neutral-400 mx-auto mb-2" />
                      <p className="text-sm font-medium text-neutral-900">
                        {receiptFile ? receiptFile.name : 'Click to upload receipt'}
                      </p>
                      <p className="text-xs text-neutral-500">
                        Supported formats: JPG, PNG, PDF (Max 10MB)
                      </p>
                    </label>
                  </div>

                  {allPendingForUpload.map((payment) => (
                    <div key={payment.id} className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
                      <div>
                        <p className="font-medium text-neutral-900">{payment.description}</p>
                        <p className="text-sm text-neutral-600">RM {payment.amount.toFixed(2)}</p>
                      </div>
                      <button
                        onClick={() => handleUploadReceipt(payment.id)}
                        disabled={!receiptFile || isLoading}
                        className="btn-primary"
                      >
                        {isLoading ? 'Uploading...' : 'Upload Receipt'}
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Payment Confirmation Modal */}
      {showConfirmationModal && paymentInitInfo?.bankAccounts && (
        <PaymentConfirmationModal
          isOpen={showConfirmationModal}
          onClose={() => setShowConfirmationModal(false)}
          onConfirm={handleConfirmPayment}
          isLoading={isLoading}
          error={error}
          paymentData={{
            amount: totalAmount,
            reference: `CASE-${caseNumber}`,
            caseNumber: caseNumber,
            dueDate: paymentInitInfo.paymentDueDate
          }}
          bankAccount={{
            accountNumber: bankAccountWithFullDetails?.accountNumber || '',
            accountHolderName: bankAccountWithFullDetails?.accountHolderName || '',
            bankName: bankAccountWithFullDetails?.bankName || ''
          }}
        />
      )}
    </div>
  )
}